import { zodResolver } from "@hookform/resolvers/zod";
import { CircleAlert } from "lucide-react";
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import {
  useController,
  useForm,
  type Control,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  type UseFormProps,
  type UseFormReturn,
  type UseFormSetError,
} from "react-hook-form";
import type { z } from "zod";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { Label } from "./label";

/**
 * react-hook-form + zod (schemas from @l2a/shared) helpers.
 *
 *   const form = useZodForm(createInvitationInputSchema, { defaultValues: { email: "", roleId: "" } });
 *   <form onSubmit={form.handleSubmit(onSubmit)}>
 *     <FormField control={form.control} name="email" label="Email" required>
 *       {(field) => <Input type="email" {...field} />}
 *     </FormField>
 *   </form>
 *
 * On a VALIDATION_FAILED response call `applyApiErrors(form, err)` to show the
 * server's field messages inline (and pass `meta: { toast: false }` to the mutation).
 */
export function useZodForm<S extends z.ZodType<FieldValues, FieldValues>>(
  schema: S,
  options: Omit<UseFormProps<z.input<S>, unknown, z.output<S>>, "resolver"> = {},
): UseFormReturn<z.input<S>, unknown, z.output<S>> {
  return useForm<z.input<S>, unknown, z.output<S>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any) as never,
    mode: "onTouched",
    ...options,
  });
}

/** Props to spread on the control: value/onChange/onBlur/name/ref + a11y wiring. */
export type FieldControlProps<TValues extends FieldValues, TName extends FieldPath<TValues>> = ControllerRenderProps<TValues, TName> & {
  id: string;
  "aria-invalid": boolean | undefined;
  "aria-describedby": string | undefined;
};

export interface FormFieldProps<TValues extends FieldValues, TName extends FieldPath<TValues>, TTransformed = TValues> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<TValues, any, TTransformed>;
  name: TName;
  label?: ReactNode;
  description?: ReactNode;
  required?: boolean;
  optional?: boolean;
  className?: string;
  /** Layout: label above (default) or control on the left with label beside (checkbox/switch rows). */
  layout?: "stacked" | "inline";
  children: (field: FieldControlProps<TValues, TName>) => ReactElement;
}

export function FormField<TValues extends FieldValues, TName extends FieldPath<TValues>, TTransformed = TValues>({
  control,
  name,
  label,
  description,
  required,
  optional,
  className,
  layout = "stacked",
  children,
}: FormFieldProps<TValues, TName, TTransformed>) {
  const { field, fieldState } = useController<TValues, TName, TTransformed>({ control, name });
  const id = useId();
  const descId = description ? `${id}-desc` : undefined;
  const errId = fieldState.error ? `${id}-err` : undefined;
  const describedBy = [descId, errId].filter(Boolean).join(" ") || undefined;
  const control_ = children({ ...field, id, "aria-invalid": fieldState.error ? true : undefined, "aria-describedby": describedBy });

  if (layout === "inline") {
    return (
      <div className={cn("flex items-start gap-3", className)}>
        <div className="pt-0.5">{control_}</div>
        <div className="min-w-0 flex-1">
          {label && (
            <Label htmlFor={id} required={required} className="cursor-pointer">
              {label}
            </Label>
          )}
          {description && (
            <p id={descId} className="mt-0.5 text-sm text-fg-muted">
              {description}
            </p>
          )}
          <FieldError id={errId} message={fieldState.error?.message} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("grid gap-1.5", className)}>
      {label && (
        <Label htmlFor={id} required={required} optional={optional}>
          {label}
        </Label>
      )}
      {control_}
      {description && !fieldState.error && (
        <p id={descId} className="text-sm text-fg-muted">
          {description}
        </p>
      )}
      <FieldError id={errId} message={fieldState.error?.message} />
    </div>
  );
}

/** Field wrapper for controls not managed by react-hook-form. */
export function Field({
  label,
  htmlFor,
  description,
  error,
  required,
  optional,
  className,
  children,
}: {
  label?: ReactNode;
  htmlFor?: string;
  description?: ReactNode;
  error?: string | null;
  required?: boolean;
  optional?: boolean;
  className?: string;
  children: ReactNode;
}) {
  /**
   * A <Label> with no `htmlFor` names nothing, which is how several controls in
   * this app ended up announced by their PLACEHOLDER ("AXISN12345678") instead
   * of by their label ("UTR"). When the caller gives a single element child and
   * has not named it some other way, wire the two together here rather than
   * asking 200 call sites to remember an id.
   */
  const generatedId = useId();
  let control = children;
  let labelFor = htmlFor;
  if (label && !htmlFor && isValidElement(children)) {
    const props = children.props as Record<string, unknown>;
    const named = props["aria-label"] !== undefined || props["aria-labelledby"] !== undefined;
    const existing = typeof props.id === "string" ? props.id : undefined;
    if (!named) {
      labelFor = existing ?? generatedId;
      if (!existing) control = cloneElement(children as ReactElement<{ id?: string }>, { id: generatedId });
    }
  }

  return (
    <div className={cn("grid gap-1.5", className)}>
      {label && (
        <Label htmlFor={labelFor} required={required} optional={optional}>
          {label}
        </Label>
      )}
      {control}
      {description && !error && <p className="text-sm text-fg-muted">{description}</p>}
      <FieldError message={error ?? undefined} />
    </div>
  );
}

export function FieldError({ id, message }: { id?: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="flex items-start gap-1.5 text-sm font-semibold text-danger-ink">
      <CircleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <span>{message}</span>
    </p>
  );
}

/** Form section with a title/description on the left and fields on the right (settings pages). */
export function FormSection({ title, description, children, className }: { title: ReactNode; description?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("grid gap-x-10 gap-y-4 py-6 first:pt-0 last:pb-0 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]", className)}>
      <div>
        <h3 className="text-base font-semibold">{title}</h3>
        {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
      </div>
      <div className="grid min-w-0 gap-4">{children}</div>
    </section>
  );
}

/**
 * Maps a VALIDATION_FAILED ApiError onto form fields. Returns true when at least
 * one message was attached to a known field; the rest go to `root.serverError`.
 */
export function applyApiErrors<TValues extends FieldValues>(
  form: { setError: UseFormSetError<TValues>; getValues: () => TValues },
  err: unknown,
): boolean {
  const setError = form.setError;
  if (!(err instanceof ApiError)) return false;
  const values = (form.getValues() ?? {}) as Record<string, unknown>;
  let mapped = false;
  const unmapped: string[] = [];
  for (const issue of err.issues) {
    const root = issue.path.split(".")[0] ?? "";
    if (issue.path && root in values) {
      setError(issue.path as FieldPath<TValues>, { type: "server", message: issue.message }, { shouldFocus: !mapped });
      mapped = true;
    } else {
      unmapped.push(issue.message);
    }
  }
  if (!mapped || unmapped.length) {
    setError("root.serverError" as FieldPath<TValues>, { type: "server", message: unmapped[0] ?? err.message });
  }
  return mapped;
}
